import { useState } from 'react';

import AnalyticsEvents from '~/analytics';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { useAddSMTEntryCommentMutation } from '~/store/services/SMT';

interface UseAddSMTCommentParams {
  patientId: string;
}

interface UseAddSMTCommentReturn {
  entryIdToAddComment: string | null;
  handleAddComment: (entryId: string) => void;
  handleCloseAddCommentModal: () => void;
  handleConfirmAddComment: (commentText: string) => Promise<void>;
  isAddCommentModalOpen: boolean;
  isLoading: boolean;
}

export const useAddSMTComment = ({
  patientId,
}: UseAddSMTCommentParams): UseAddSMTCommentReturn => {
  const [entryIdToAddComment, setEntryIdToAddComment] = useState<string | null>(
    null,
  );
  const { trackEvent } = useAnalytics();
  const { logError } = useDatadog();
  const [addSMTEntryComment, { isLoading }] = useAddSMTEntryCommentMutation();

  const handleAddComment = (entryId: string) => {
    setEntryIdToAddComment(entryId);
    trackEvent(AnalyticsEvents.addCommentButtonClicked, {
      entryId,
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });
  };

  const handleConfirmAddComment = async (commentText: string) => {
    if (!entryIdToAddComment) return;

    trackEvent(AnalyticsEvents.addCommentConfirmed, {
      entryId: entryIdToAddComment,
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });

    try {
      await addSMTEntryComment({
        comment: commentText,
        patientId,
        smtEntryId: entryIdToAddComment,
      }).unwrap();
      setEntryIdToAddComment(null);
    } catch (error) {
      logError(error, {
        action: 'addSMTComment',
        context: 'Failed to add comment to SMT entry',
        entryId: entryIdToAddComment,
        patientId,
      });
    }
  };

  const handleCloseAddCommentModal = () => {
    if (entryIdToAddComment) {
      trackEvent(AnalyticsEvents.addCommentCanceled, {
        entryId: entryIdToAddComment,
        patientId,
        source: AnalyticsSourceValues.SMT_TAB,
      });
    }
    setEntryIdToAddComment(null);
  };

  return {
    entryIdToAddComment,
    handleAddComment,
    handleCloseAddCommentModal,
    handleConfirmAddComment,
    isAddCommentModalOpen: entryIdToAddComment !== null,
    isLoading,
  };
};
